import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtDateTime } from "@/lib/checkins";
import { errMsg } from "./ResolveDialog";

/** Practitioner interpretation + program adjustments; saving resolves the diary's open alerts. */
export function DiaryReviewForm({ sub, readOnly, onDone }) {
  const rev = sub.clinician_review;
  const [editing, setEditing] = useState(!rev);
  const [interp, setInterp] = useState(rev?.interpretation || "");
  const [adj, setAdj] = useState(rev?.adjustments || "");
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await api.post(`/checkin-submissions/${sub.id}/diary-review`, { interpretation: interp.trim(), adjustments: adj.trim() || null });
      toast.success("Diary review saved", { description: "Open diary alerts for this diary are resolved." });
      setEditing(false);
      onDone();
    } catch (e) {
      toast.error("Couldn't save review", { description: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-2" data-testid="diary-review">
      {rev && !editing && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-zinc-900" data-testid="diary-review-saved">
          <div className="text-xs text-emerald-900">Reviewed by {rev.by_name} · {fmtDateTime(rev.at)}</div>
          <p className="mt-1"><b>Interpretation:</b> {rev.interpretation}</p>
          {rev.adjustments && <p className="mt-1"><b>Program adjustments:</b> {rev.adjustments}</p>}
          {!readOnly && <Button size="sm" variant="ghost" className="mt-1 h-7 px-2" onClick={() => setEditing(true)} data-testid="diary-review-edit">Update review</Button>}
        </div>
      )}
      {!rev && readOnly && <p className="text-sm text-zinc-600">Not reviewed yet.</p>}
      {editing && !readOnly && (
        <div className="space-y-2">
          <div><Label className="text-xs">Clinical interpretation</Label>
            <Textarea rows={3} className="mt-1" value={interp} onChange={(e) => setInterp(e.target.value)} placeholder="e.g. Pacing well; fatigue peaks after afternoon activity" data-testid="diary-review-interpretation" /></div>
          <div><Label className="text-xs">Program adjustments (optional)</Label>
            <Textarea rows={2} className="mt-1" value={adj} onChange={(e) => setAdj(e.target.value)} placeholder="e.g. Move walks to mornings, cap at 15 min" data-testid="diary-review-adjustments" /></div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy || interp.trim().length < 3} data-testid="diary-review-save">Save review</Button>
            {rev && <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="diary-review-cancel">Cancel</Button>}
          </div>
        </div>
      )}
    </div>
  );
}
